"use client";

import { type CSSProperties, type ReactNode } from "react";

import { TextBlock as TextBlockType, TextColumn } from "@/lib/utils/lomdaV3/types";
import { t } from "@/lib/localization";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import {
    useBlockEditable,
    useBlockThumbnail,
} from "../../../hooks/useBlockEditable";
import { useResolvedInnerGap } from "../../../hooks/useGridGap";
import { headingFieldProps, headingHtmlProps } from "../../../lib/ownFonts";
import RichText from "./richText";
import RichHtml from "../interactive/richHtml";
import QuoteVariant from "./quoteVariant";
import LinkVariant from "./linkVariant";
import TableBlock from "./tableBlock";

export default function TextBlock({ block }: { block: TextBlockType }) {
    const selectedBlockId = useLomdaV3Editor((s) => s.selectedBlockId);
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);

    const editable = useBlockEditable();
    const thumbnail = useBlockThumbnail();
    const innerGap = useResolvedInnerGap();
    const isSelected = selectedBlockId === block.id;
    const data = block.data;

    // Link curation has no rich-text editing, handle it on its own.
    if (data.variant === "link") {
        return <LinkVariant block={block} />;
    }

    const isColumns =
        data.variant === "multi-columns" ||
        data.variant === "two-columns" ||
        data.variant === "three-columns";

    const updateColumn = (index: number, patch: Partial<TextColumn>) => {
        if (!isColumns) return;
        const columns = data.columns.map((c, i) =>
            i === index ? { ...c, ...patch } : c
        );
        updateBlock(block.id, { columns });
    };

    const common = { editable, useGlobalToolbar: true, blockId: block.id };

    let body: ReactNode = null;
    switch (data.variant) {
        case "text":
            body = thumbnail ? (
                <RichHtml html={data.html} />
            ) : (
                // Figma "Conteiner": 120px tall, 16px padding. RichText's shared
                // wrapper already adds px-2 py-1, so this tops it up: 4+12=16px
                // top, 8+8=16px start, and 8+112=120px total.
                // No forced min-height/padding: the editor is a 1-line minimum
                // (`.lomda-v3-rich-text` min-height: 1.5em) and grows with content,
                // so a trailing empty line can be deleted.
                <RichText
                    initialHtml={data.html}
                    placeholder={t("lomda-v3-editor.block.text.placeholder")}
                    autoFocus={isSelected && !data.html}
                    onCommit={(html) => updateBlock(block.id, { html })}
                    {...common}
                />
            );
            break;
        case "heading-text":
            body = thumbnail ? (
                <div className="flex flex-col" style={{ gap: innerGap }}>
                    <RichHtml {...headingHtmlProps(data)} />
                    <RichHtml html={data.text} />
                </div>
            ) : (
                <div className="flex flex-col" style={{ gap: innerGap }}>
                    <RichText
                        {...headingFieldProps(data, (patch) =>
                            updateBlock(block.id, patch)
                        )}
                        placeholder={t("lomda-v3-editor.text-variant.heading-placeholder")}
                        {...common}
                    />
                    <RichText
                        initialHtml={data.text}
                        placeholder={t("lomda-v3-editor.block.text.placeholder")}
                        onCommit={(text) => updateBlock(block.id, { text })}
                        {...common}
                    />
                </div>
            );
            break;
        case "multi-columns":
        case "two-columns":
        case "three-columns":
            body = (
                <div
                    className="lomda-v3-columns gap-6"
                    style={
                        {
                            "--lomda-cols": data.columns.length,
                        } as CSSProperties
                    }
                >
                    {data.columns.map((col, i) => (
                        <div
                            key={i}
                            className="lomda-v3-card-surface flex flex-col rounded-2xl border p-5 shadow-sm"
                            style={{ gap: innerGap }}
                        >
                            {thumbnail ? (
                                <>
                                    <RichHtml {...headingHtmlProps(col)} />
                                    <RichHtml html={col.text} />
                                </>
                            ) : (
                                <>
                                    <RichText
                                        {...headingFieldProps(col, (patch) =>
                                            updateColumn(i, patch)
                                        )}
                                        placeholder={t("lomda-v3-editor.text-variant.heading-placeholder")}
                                        {...common}
                                    />
                                    <RichText
                                        initialHtml={col.text}
                                        placeholder={t("lomda-v3-editor.block.text.placeholder")}
                                        onCommit={(text) => updateColumn(i, { text })}
                                        {...common}
                                    />
                                </>
                            )}
                        </div>
                    ))}
                </div>
            );
            break;
        case "table":
            body = <TableBlock block={block} editable={editable} />;
            break;
        case "quote":
            body = <QuoteVariant block={block} editable={editable} />;
            break;
    }

    return (
        <div className="relative w-full">{body}</div>
    );
}
